import { venue as api, SIGNED_IN_EVENT } from './api.js';
import { markSaved, savedLoaded } from './saved.js';
import { markLiked, likedLoaded } from './liked.js';

/* ══════════════════════════════════════════════════════════════════════════
 * WHAT THEY TAPPED BEFORE SIGNING IN IS DONE FOR THEM AFTER.
 *
 * Al-Noor, 21 Oct 2026: he hearted a restaurant, was asked to sign in, signed
 * in, came back — and the restaurant was not saved. The gate brought him back
 * to the right screen and then forgot why he had left it, so he had to tap
 * the heart a second time. Uber Eats and Airbnb finish the action you were
 * taking once you are signed in; so does Eleat now.
 *
 * Kept for this visit only (sessionStorage) and for 30 minutes: an action
 * somebody walked away from yesterday is not one they still want done.
 * ══════════════════════════════════════════════════════════════════════════ */

const KEY = 'eleat.pendingAction';
const REVIEW_KEY = 'eleat.pendingReview';
const FRESH_MS = 30 * 60 * 1000;

let waiting = null;   // what the open sheet was asked for, until Continue

/* the gate's sheet holds this while it is open */
export function holdPending(p) { waiting = p || null; }

/* Continue pressed — keep it across the sign-in; Not now — forget it */
export function keepPending() {
  try {
    if (!waiting) return;
    if (waiting.review) sessionStorage.setItem(REVIEW_KEY, JSON.stringify({ id: waiting.review, at: Date.now() }));
    else sessionStorage.setItem(KEY, JSON.stringify({ ...waiting, at: Date.now() }));
  } catch { /* storage refused — they tap again, as before */ }
  waiting = null;
}
export function dropPending() { waiting = null; }

function take(key) {
  try {
    const raw = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    const p = raw ? JSON.parse(raw) : null;
    return p && Date.now() - (p.at || 0) < FRESH_MS ? p : null;
  } catch { return null; }
}

/* the review sheet asks this when its venue page opens */
export function takePendingReview(venueId) {
  try {
    const raw = sessionStorage.getItem(REVIEW_KEY);
    const p = raw ? JSON.parse(raw) : null;
    if (!p || p.id !== venueId) return false;
    sessionStorage.removeItem(REVIEW_KEY);
    return Date.now() - (p.at || 0) < FRESH_MS;
  } catch { return false; }
}

/* ⚠️ THE WRITE GOES FIRST, THE HEART SECOND. Signing in also re-reads the
   saved list; drawn before that answer lands, the heart would be wiped by a
   list read a moment before the save. So: save, wait for the list, then
   mark it. */
function runPending() {
  const p = take(KEY);
  if (!p) return;
  if (p.save) {
    api.save(p.save)
      .then(() => savedLoaded())
      .then(() => markSaved(p.save, true))
      .catch(() => {});
  }
  if (p.like) {
    api.likeFeed(p.like, true)
      .then(() => likedLoaded())
      .then(() => markLiked(p.like, true))
      .catch(() => {});
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener(SIGNED_IN_EVENT, runPending);
}
